import type { AxiosError } from 'axios'
import { BaseProxy, type ProxyMethods } from '@/proxies/Proxy'
import Axios from '@/services/Axios'

// Dev only: VITE_APP_API_LOCATION is the real Stidner API, and VITE_TRACKING_FALLBACK_LOCATION lists more sources
// (comma separated: core-dev, then the assistant's demo parcels) that are asked in order when a shipment is not
// found. Unset in production: requests are unchanged.
const fallbackBases = (): string[] =>
  (import.meta.env.VITE_TRACKING_FALLBACK_LOCATION || '')
    .split(',')
    .map((base: string) => base.trim())
    .filter(Boolean)
const foundOn = new Map<string, string>()

const isNotFound = (e: unknown) => (e as AxiosError).response?.status === 404

export default class TrackingProxy extends BaseProxy implements ProxyMethods {
  public endpoint = 'tracking/v2'

  public async getTrackingData(identifier: string) {
    const path = `/${this.endpoint}/by-identifier/${identifier}`
    const fallbacks = fallbackBases()
    if (!fallbacks.length) return this.submit('get', path)
    try {
      return (await Axios.get(path)).data
    } catch (e) {
      if (!isNotFound(e)) throw (e as AxiosError).response?.data ?? e
    }
    for (const [i, base] of fallbacks.entries()) {
      try {
        const data = (await Axios.get(`${base}${path}`)).data
        foundOn.set(identifier, base)
        return data
      } catch (e) {
        if (!isNotFound(e) || i === fallbacks.length - 1) throw (e as AxiosError).response?.data ?? e
      }
    }
  }

  public async getProofOfDelivery(identifier: string) {
    const path = `/proof-of-delivery/${identifier}`
    const base = foundOn.get(identifier)
    if (base) return (await Axios.get(`${base}${path}`)).data
    return this.submit('get', path)
  }
}
